import { useMemo, useState } from 'react';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import { Alert, Flex, Select, Skeleton, Text } from '@backstage/ui';
import { useGitLabProjects } from './hooks/useGitLabProjects';
import {
  GitLabProject,
  GitLabProjectData,
  GitLabProjectPickerProps,
} from './types';
import { FieldHint } from '../../components/FieldHint';
import '../../select-overlay.css';

const MAX_VISIBLE_OPTIONS = 20;

function projectToData(project: GitLabProject): GitLabProjectData {
  return {
    projectId: project.project_id,
    path: project.path,
    pathWithNamespace: project.path_with_namespace,
    name: project.name,
    webUrl: project.web_url,
    defaultBranch: project.default_branch,
  };
}

export const GitLabProjectPicker = ({
  onChange,
  required,
  schema,
  uiSchema,
  formData,
  rawErrors,
}: FieldExtensionComponentProps<
  GitLabProjectData | GitLabProjectData[],
  GitLabProjectPickerProps
>) => {
  const config: GitLabProjectPickerProps = uiSchema?.['ui:options'] || {};
  const host = config.host;
  const isMultiple = schema.type === 'array';
  const { projects, loading, error } = useGitLabProjects(host);
  const [searchTerm, setSearchTerm] = useState('');

  const selectedIds = useMemo(() => {
    if (isMultiple) {
      if (Array.isArray(formData)) {
        return formData.map((project: GitLabProjectData) =>
          String(project.projectId),
        );
      }
      if (formData?.projectId) {
        return [String(formData.projectId)];
      }
      return [];
    }

    const projectId = Array.isArray(formData)
      ? formData[0]?.projectId
      : formData?.projectId;
    return projectId !== undefined && projectId !== null
      ? [String(projectId)]
      : [];
  }, [formData, isMultiple]);

  const options = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    const selectedSet = new Set(selectedIds);

    if (query) {
      const matches = projects.filter(p => {
        const name = p.name?.toLowerCase() ?? '';
        const namespace = p.path_with_namespace?.toLowerCase() ?? '';
        const id = String(p.project_id);
        return (
          name.includes(query) ||
          namespace.includes(query) ||
          id.includes(query)
        );
      });

      return matches.slice(0, MAX_VISIBLE_OPTIONS).map(p => ({
        id: String(p.project_id),
        label: `${p.name} (${p.path_with_namespace})`,
      }));
    }

    const result: Array<{ id: string; label: string }> = [];
    const addedIds = new Set<string>();

    // 1. Ensure selected projects are in options so their labels are resolved
    for (const p of projects) {
      const idStr = String(p.project_id);
      if (selectedSet.has(idStr)) {
        addedIds.add(idStr);
        result.push({
          id: idStr,
          label: `${p.name} (${p.path_with_namespace})`,
        });
      }
    }

    // 2. Add projects up to MAX_VISIBLE_OPTIONS
    for (const p of projects) {
      const idStr = String(p.project_id);
      if (!addedIds.has(idStr)) {
        addedIds.add(idStr);
        result.push({
          id: idStr,
          label: `${p.name} (${p.path_with_namespace})`,
        });
        if (result.length >= MAX_VISIBLE_OPTIONS) {
          break;
        }
      }
    }

    return result;
  }, [projects, searchTerm, selectedIds]);

  const handleSingleChange = (key: unknown) => {
    let rawKey = key;
    if (key instanceof Set) {
      rawKey = Array.from(key)[0];
    } else if (Array.isArray(key)) {
      rawKey = key[0];
    }
    const nextId = String(rawKey ?? '');
    if (!nextId) {
      onChange(undefined);
      return;
    }
    const project = projects.find(p => String(p.project_id) === nextId);
    onChange(project ? projectToData(project) : undefined);
  };

  const handleMultipleChange = (keys: unknown) => {
    let nextIds: string[] = [];
    if (Array.isArray(keys)) {
      nextIds = keys.map(String);
    } else if (keys instanceof Set) {
      nextIds = Array.from(keys).map(String);
    } else if (keys) {
      nextIds = [String(keys)];
    }

    const data = nextIds
      .map(id => projects.find(p => String(p.project_id) === id))
      .filter((p): p is GitLabProject => p !== undefined)
      .map(projectToData);
    onChange(data.length > 0 ? data : undefined);
  };

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      setSearchTerm('');
    }
  };

  const hasError = (rawErrors?.length ?? 0) > 0 || !!error;
  const title =
    schema.title ||
    (isMultiple
      ? 'Selecione os Projetos GitLab'
      : 'Selecione o Projeto GitLab');

  const errorHint = rawErrors?.join(', ') || 'Campo obrigatório';
  const selectedProject = projects.find(
    p => String(p.project_id) === selectedIds[0],
  );
  const singleHint = selectedProject
    ? `ID: ${selectedProject.project_id} | Branch padrão: ${
        selectedProject.default_branch || 'N/A'
      }`
    : undefined;

  let multipleHint: string | undefined;
  if (selectedIds.length > 0) {
    multipleHint = `${selectedIds.length} projeto(s) selecionado(s)`;
  } else if (required) {
    multipleHint = 'Selecione pelo menos um projeto';
  }

  if (loading) {
    return (
      <Flex direction="column">
        <Text variant="title-small">{title}</Text>
        <Skeleton width="100%" height="2.5rem" />
      </Flex>
    );
  }

  if (error) {
    return (
      <Flex direction="column">
        <Text variant="title-small">{title}</Text>
        <Alert
          status="danger"
          icon
          title="Erro ao carregar projetos"
          description={error.message}
        />
      </Flex>
    );
  }

  if (isMultiple) {
    return (
      <Flex direction="column">
        <Select<'multiple'>
          label={title}
          selectionMode="multiple"
          name="gitlab-projects-select"
          size="medium"
          placeholder={config.placeholder ?? 'Selecione os projetos...'}
          value={selectedIds}
          isRequired={required}
          isDisabled={loading}
          onOpenChange={handleOpenChange}
          search={{
            mode: 'server',
            inputValue: searchTerm,
            onInputChange: setSearchTerm,
            placeholder: 'Digite para filtrar...',
          }}
          options={options}
          onChange={handleMultipleChange}
        />
        <FieldHint error={hasError}>
          {hasError && !error ? errorHint : multipleHint}
        </FieldHint>
      </Flex>
    );
  }

  return (
    <Flex direction="column">
      <Select<'single'>
        label={title}
        selectionMode="single"
        name="gitlab-project-select"
        size="medium"
        placeholder={config.placeholder ?? 'Selecione o projeto...'}
        value={selectedIds[0] || null}
        isRequired={required}
        isDisabled={loading}
        onOpenChange={handleOpenChange}
        search={{
          mode: 'server',
          inputValue: searchTerm,
          onInputChange: setSearchTerm,
          placeholder: 'Digite para filtrar...',
        }}
        options={options}
        onChange={handleSingleChange}
      />
      <FieldHint error={hasError}>
        {hasError && !error ? errorHint : singleHint}
      </FieldHint>
    </Flex>
  );
};
